// import components
import { Checkbox } from '@mui/material';

// import constants
import { BACKLOG_FILTER_SUMMARY_MAX_CHARS } from '../../Backlog.consts';

// import utils
import { entityFiltersForView } from '../../Backlog.utils';

// import styles
import { BacklogFilterTagSummary } from '../../Backlog.styles';

// import types
import type { HTMLAttributes, Key } from 'react';
import type { PlanningEntityType } from 'generated/index';
import type {
  BacklogEmployeeViewFilters,
  BacklogOrganizationalUnitId,
  BacklogEntityFilter
} from '../../Backlog.types';
import type { AutocompleteRenderOptionState } from '@mui/material';

/**
 * What a filter field shows for its selection. One pick is named, so the planner can see *what* is
 * filtered; past that the names are dropped for a plain count — a planner picking several activities or
 * several employees would otherwise be reading a wall of chips in a field one line tall.
 *
 * A single name longer than {@link BACKLOG_FILTER_SUMMARY_MAX_CHARS} is reported as a count too: it
 * cannot be read in the space available, and half a name is worth less than knowing one thing is picked.
 *
 * Always a single element, never MUI's chip per option: the field must stay one line however much is
 * picked, and the summary ellipsises rather than wrapping (see {@link BacklogFilterTagSummary}).
 */
export const renderFilterTags =
  <Option,>(
    getOptionLabel: (option: Option) => string,
    selectedLabel: string
  ) =>
  (selected: Option[]) => {
    const onlyPick = selected.length === 1 ? getOptionLabel(selected[0]) : null;
    const fits =
      onlyPick !== null && onlyPick.length <= BACKLOG_FILTER_SUMMARY_MAX_CHARS;

    return (
      <BacklogFilterTagSummary>
        {fits ? onlyPick : `${selected.length} ${selectedLabel}`}
      </BacklogFilterTagSummary>
    );
  };

/**
 * A filter option with a checkbox, and a list that stays open while you tick.
 *
 * Every one of these fields takes several picks, and MUI closes its list after each one by default — so a
 * planner had to reopen the field per activity. The checkbox is what says the field is a multiple choice in
 * the first place; keeping the list open is the field's own `disableCloseOnSelect`.
 *
 * Rendered here rather than through the shared MuiAutocompleteOption: that component keeps the props it is
 * handed for itself, so the `li` loses the role, id and aria-selected MUI put there — the list would
 * announce as a listbox with no options in it. Its search-term highlighting is what we give up for that.
 */
export const renderFilterOption =
  <Option,>(getOptionLabel: (option: Option) => string) =>
  (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: Option,
    { selected }: AutocompleteRenderOptionState
  ) => {
    // MUI puts the list key in the same object as the DOM props, and React refuses a spread key.
    const { key, ...optionProps } = props as HTMLAttributes<HTMLLIElement> & {
      key?: Key;
    };

    return (
      <li key={key} {...optionProps}>
        <Checkbox size="small" checked={selected} />
        {getOptionLabel(option)}
      </li>
    );
  };

/**
 * How many filters the toolbar's badge reports: one per field that has something in it, not one per value —
 * two plan units and three patients is two filters, not five.
 *
 * Counted over the fields the view on screen actually offers, since a remembered pick from another view
 * narrows nothing here and the popover does not show it. The plan unit is counted in every view.
 */
export const countActiveFilters = ({
  rowEntityType,
  organizationalUnitIds,
  entityFilters,
  employeeViewFilters
}: {
  rowEntityType: PlanningEntityType;
  organizationalUnitIds: BacklogOrganizationalUnitId[];
  entityFilters: BacklogEntityFilter[];
  employeeViewFilters: BacklogEmployeeViewFilters;
}): number => {
  const setFields =
    rowEntityType === 'EMPLOYEE'
      ? [
          entityFiltersForView(entityFilters, rowEntityType),
          employeeViewFilters.functions
        ]
      : [entityFiltersForView(entityFilters, rowEntityType)];

  return (
    (organizationalUnitIds.length > 0 ? 1 : 0) +
    setFields.filter(({ length }) => length > 0).length
  );
};
